/**
 * NotificationBell.tsx
 *
 * Bell icon with unread badge. Clicking opens a dropdown with the 10 most
 * recent notifications. Closes on outside click and Escape.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { Paper, Box, Typography, CircularProgress } from '@mui/material';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationItem from './NotificationItem';
import type { UseNotificationsState } from '../../hooks/useNotifications';

// ── Styled ────────────────────────────────────────────────────────────────────

const BellButton = styled('button')`
  all: unset;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: var(--radius-full);
  cursor: pointer;
  color: var(--color-text-secondary);
  transition:
    background var(--duration-shortest) ease,
    color var(--duration-shortest) ease;
  flex-shrink: 0;
  &:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
  }
  svg {
    font-size: 22px;
  }
`;

const Badge = styled('span')`
  position: absolute;
  top: 2px;
  right: 2px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 100px;
  background: var(--color-primary);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  box-sizing: border-box;
  pointer-events: none;
`;

const Dropdown = styled(Paper)`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: min(360px, 90vw);
  max-height: 480px;
  display: flex;
  flex-direction: column;
  background: var(--color-bg-surface) !important;
  border: 1px solid var(--color-border-default) !important;
  border-radius: var(--radius-lg) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.14) !important;
  z-index: 300;
  overflow: hidden;

  /* On narrow viewports the bell sits very close to the right edge and the
   * absolute-positioned dropdown spills off the left. Pin to the viewport
   * instead — right: 5vw + max-width 90vw guarantees a 5vw gutter on both
   * sides regardless of where the bell sits in the nav. Top references the
   * 60px nav height plus an 8px gap to mirror the desktop offset. */
  @media (max-width: 640px) {
    position: fixed;
    top: 68px;
    right: 5vw;
    left: auto;
    width: min(360px, 90vw);
    max-width: 90vw;
  }
` as typeof Paper;

const DropdownHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-3) var(--spacing-4);
  border-bottom: 1px solid var(--color-border-default);
  flex-shrink: 0;
`;

const MarkAllBtn = styled('button')`
  all: unset;
  font-size: 12px;
  color: var(--color-primary);
  cursor: pointer;
  padding: 2px 4px;
  border-radius: var(--radius-sm);
  &:hover {
    text-decoration: underline;
  }
  &:disabled {
    color: var(--color-text-disabled);
    cursor: default;
    text-decoration: none;
  }
`;

const ScrollArea = styled(Box)`
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`;

const EmptyState = styled(Box)`
  padding: var(--spacing-8) var(--spacing-4);
  text-align: center;
  color: var(--color-text-disabled);
  font-size: 13px;
`;

const DropdownFooter = styled(Link)`
  display: block;
  text-align: center;
  padding: var(--spacing-2-5) var(--spacing-4);
  font-size: 13px;
  color: var(--color-primary);
  text-decoration: none;
  border-top: 1px solid var(--color-border-default);
  flex-shrink: 0;
  transition: background var(--duration-shortest) ease;
  &:hover {
    background: var(--color-bg-hover);
  }
`;

const WrapperBox = styled(Box)`
  position: relative;
  display: flex;
  align-items: center;
`;

// ── Component ─────────────────────────────────────────────────────────────────

interface NotificationBellProps {
  notifications: UseNotificationsState;
}

export default function NotificationBell({ notifications }: NotificationBellProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const hasLoaded = useRef(false);

  const {
    unreadCount,
    notifications: items,
    listLoading,
    markRead,
    markAllRead,
    loadNotifications,
  } = notifications;

  const handleMarkAllRead = async () => {
    await markAllRead();
  };

  useEffect(() => {
    if (open && !hasLoaded.current) {
      hasLoaded.current = true;
      loadNotifications(1);
    } else if (open) {
      loadNotifications(1);
    }
  }, [open, loadNotifications]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const displayItems = items.slice(0, 10);
  const badgeLabel = unreadCount > 99 ? '99+' : unreadCount;

  return (
    <WrapperBox ref={wrapperRef}>
      <BellButton
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((o) => !o)}
      >
        {open ? <NotificationsIcon /> : <NotificationsNoneIcon />}
        {unreadCount > 0 && <Badge aria-hidden="true">{badgeLabel}</Badge>}
      </BellButton>

      {open && (
        <Dropdown>
          <DropdownHeader>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text-primary)' }}>
              Notifications
            </Typography>
            <MarkAllBtn onClick={handleMarkAllRead} disabled={unreadCount === 0}>
              Mark all read
            </MarkAllBtn>
          </DropdownHeader>

          <ScrollArea>
            {listLoading && displayItems.length === 0 ? (
              <EmptyState>
                <CircularProgress size={20} sx={{ color: 'var(--color-text-disabled)' }} />
              </EmptyState>
            ) : displayItems.length === 0 ? (
              <EmptyState>You're all caught up!</EmptyState>
            ) : (
              displayItems.map((n) => (
                <NotificationItem
                  key={n.id}
                  notification={n}
                  onRead={markRead}
                  onNavigate={() => setOpen(false)}
                />
              ))
            )}
          </ScrollArea>

          <DropdownFooter to="/notifications" onClick={() => setOpen(false)}>
            See all notifications
          </DropdownFooter>
        </Dropdown>
      )}
    </WrapperBox>
  );
}
